import React, {useEffect, useState} from "react";
import { useSelector, useDispatch } from "react-redux";
import { burnModalHide } from "../../redux/counterSlice";
import {createPledge, getWalletBalances} from "../../utils/api";
import {getAccountHolderId, getUserId} from "../../utils/jwtUtil";
import Spinner from "../Spinner";
import {Alert} from "@mui/material";

const BurnModal = () => {
  const { burnModal, projectData } = useSelector((state) => state.counter);
  const dispatch = useDispatch();
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [claim, setClaim] = useState("Our organisation has contributed to climate action by purchasing CADRs each equivalent to 1 tonne year of CO2e reduced from the atmosphere, or\n" +
      "avoided emissions."); // State for textarea value
  const [balanceForProject, setBalanceForProject] = useState(0);
  const [balanceForProjectString, setBalanceForProjectString] = useState("");
  const [cadrsAmount, setCadrsAmount] = useState(1); // State for CADRs amount
  const [accountHolderId, setAccountHolderId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const { data: walletBalancesData, error: walletBalancesError} = getWalletBalances(accountHolderId);

  useEffect(() => {
    setAccountHolderId(getAccountHolderId());
  },[]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(""); // Clear the success message after 5 seconds
      }, 5000);

      return () => clearTimeout(timer); // Clear the timer on component unmount
    }
  }, [successMessage]);

  useEffect(() => {
    if (!walletBalancesData) return;
    if (!projectData) return;
    const projectBalanceForUser = walletBalancesData.find(x => x.projectId === projectData.projectId && x.walletAccountType === "Server");
    if (projectBalanceForUser) {
      setBalanceForProject(projectBalanceForUser.balance);

      const userLocale = navigator.language || 'en-AU';
      setBalanceForProjectString(new Intl.NumberFormat(userLocale).format(projectBalanceForUser.balance));
    }
  }, [walletBalancesData, projectData]);

  async function handleSubmit(e) {
    e.preventDefault();
    setIsButtonDisabled(true);
    setIsLoading(true); // Show spinner
    try {
      await createPledge({
        projectId: projectData.projectId,
        claim: claim,
        cadrsPledged: Number(cadrsAmount), // Use the state variable here
      });
      setSuccessMessage("Pledge successful!");
    } catch (error) {
      console.error("Error creating pledge:", error);
    } finally {
      setIsButtonDisabled(false);
      setIsLoading(false); // Hide spinner
    }
  }

  function handleClaim(e) {
    setClaim(e.target.value); // Update the state with textarea value
  }

  function handleCadrsAmountChange(e) {
    setCadrsAmount(e.target.value); // Update the state with input value
  }

  if (!projectData) return;
  if (!walletBalancesData) return;

  return (
    <div>
      <div className={burnModal ? "modal fade show block" : "modal fade"}>
        <div className="modal-dialog max-w-2xl">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="burnNowModalLabel">
                Pledge
              </h5>
              <button
                  type="button"
                  className="btn-close"
                  onClick={() => dispatch(burnModalHide())}
              >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="24"
                    height="24"
                    className="fill-jacarta-700 h-6 w-6 dark:fill-white"
                >
                  <path fill="none" d="M0 0h24v24H0z"/>
                  <path
                      d="M12 10.586l4.95-4.95 1.414 1.414-4.95 4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95 4.95-1.414-1.414 4.95-4.95-4.95-4.95L7.05 5.636z"/>
                </svg>
              </button>
            </div>
            <div className="modal-body p-6">
              <div className="mb-2 flex items-center justify-between">
                <span className="font-display text-jacarta-700 text-sm font-semibold dark:text-white">
                  Statement
                </span>
              </div>

              <div
                className="dark:border-jacarta-600 border-jacarta-100 relative mb-2 flex items-center overflow-hidden rounded-lg border">
              <div className="border-jacarta-100 bg-jacarta-50 flex flex-1 items-center self-stretch border-r px-2">
                
                <span className="font-display text-jacarta-700 text-sm">
                  Claim
                </span>
              </div>

              <textarea
                  className="pledgeStatement focus:ring-accent py-3 h-100 w-full flex-[3] border-0 focus:ring-inse dark:text-jacarta-700"
                  required
                  rows="5"
                  value={claim} // Bind the state to textarea
                  onChange={handleClaim} // Update state on change
              ></textarea>
            </div>

            <div className="mb-2 flex items-center justify-between">
              <span className="font-display text-jacarta-700 text-sm font-semibold dark:text-white">
                Amount
              </span>
            </div>

            <div
                className="dark:border-jacarta-600 border-jacarta-100 relative mb-2 flex items-center overflow-hidden rounded-lg border">
              <div className="border-jacarta-100 bg-jacarta-50 flex flex-1 items-center self-stretch border-r px-2">
                <span className="font-display text-jacarta-700 text-sm">
                    CADRs
                  </span>
              </div>

              <input
                  type="number"
                  className="focus:ring-accent h-12 w-full flex-[3] border-0 focus:ring-inse dark:text-jacarta-700"
                  placeholder="Amount"
                  value={cadrsAmount} // Bind the state to input
                  onBlur={() => {
                    if (cadrsAmount > balanceForProject) {
                      setCadrsAmount(balanceForProject);
                    }
                    else if (cadrsAmount < 1) {
                      setCadrsAmount(1);
                    }
                  }}
                  onChange={handleCadrsAmountChange} // Update state on change
              />
            </div>

            <div className="text-right">
                <span className="dark:text-jacarta-400 text-sm">
                  Balance: {balanceForProjectString} CADRs
                </span>
            </div>
          </div>

          <div className="modal-footer">
            <div className="flex items-center justify-center space-x-4">
              <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isButtonDisabled}
                  className="bg-accent shadow-accent-volume hover:bg-accent-dark rounded-full py-3 px-8 text-center font-semibold text-white transition-all"
              >
                {isLoading ? (
                    <div className="flex items-center justify-center">
                      <Spinner/> {/* Display the spinner component */}
                      <span className="ml-2">In progress...</span>
                    </div>
                ) : (
                    "Pledge"
                )}
              </button>
            </div>
          </div>
            {successMessage && (
                <Alert severity="success">
                  {successMessage}
                </Alert>
            )}
          </div>
          
        </div>
      </div>
    </div>
  )
      ;
};

export default BurnModal;